import React from "react";
import { Box, Typography, Checkbox } from "@mui/material";
import RestaurantMenuOutlinedIcon from "@mui/icons-material/RestaurantMenuOutlined";
import { useTranslation } from "react-i18next";

/**
 * CategoryList - Renders the list of categories with item counts and checkboxes
 *
 * @param {Array}    categories          - Array of { id, name, items }
 * @param {string}   selectedCategoryId  - Currently selected category ID
 * @param {Function} onSelectCategory    - Category selection handler
 * @param {Set}      selectedCategoryIds - Set of checked category IDs
 * @param {Function} onToggleCategory    - (e, catId) => void — toggle checkbox
 */
const CategoryList = ({
  categories,
  selectedCategoryId,
  onSelectCategory,
  selectedCategoryIds,
  onToggleCategory,
  isCatalog = false,
}) => {
  const { t } = useTranslation("dashboard");
  const label = isCatalog ? t("catalogue", "catalogue") : t("menu", "menu");

  return (
    <Box>
      <Typography
        sx={{
          fontSize: "0.85rem",
          fontWeight: 700,
          color: "#111827",
          textTransform: "lowercase",
          mb: 1,
        }}
      >
        {t("ocrCategoryListTitle", { label })}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {categories.map((category) => {
          const isSelected = selectedCategoryId === category.id;
          const isChecked = selectedCategoryIds?.has(category.id) ?? true;

          return (
            <Box
              key={category.id}
              onClick={() => onSelectCategory(category.id)}
              sx={{
                display: "flex",
                alignItems: "center",
                px: 0.5,
                py: 0.75,
                borderRadius: 2,
                cursor: "pointer",
                bgcolor: isSelected ? "#F3EEFF" : "transparent",
                border: isSelected
                  ? "1px solid #E9E3FF"
                  : "1px solid transparent",
                transition: "all 0.15s ease",
                "&:hover": {
                  bgcolor: isSelected ? "#F3EEFF" : "#f9fafb",
                },
              }}
            >
              {/* Checkbox */}
              <Checkbox
                checked={isChecked}
                onChange={(e) => onToggleCategory?.(e, category.id)}
                size="small"
                sx={{
                  p: 0.25,
                  mr: 0.5,
                  color: "#7C3AED",
                  "&.Mui-checked": { color: "#7C3AED" },
                }}
              />

              <RestaurantMenuOutlinedIcon
                sx={{
                  fontSize: 16,
                  color: isSelected ? "#7C3AED" : "#9ca3af",
                  mr: 0.75,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  fontWeight: isSelected ? 700 : 500,
                  color: isSelected ? "#7C3AED" : "#374151",
                  textTransform: "lowercase",
                  flex: 1,
                }}
              >
                {category.name}
              </Typography>

              <Typography
                sx={{
                  fontSize: "0.72rem",
                  color: "#9ca3af",
                  textTransform: "lowercase",
                  flexShrink: 0,
                }}
              >
                {t("ocrCategoryListItemCount", { count: category.items.length })}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default CategoryList;
